<template>
  <section
    v-if="fields && items.length"
    :class="rootClass"
    :style="rootStyle"
    :id="rendering?.dataSource"
  >
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-feature-carousel__inner">
        <div ref="swiperEl" class="swiper c-feature-carousel__swiper">
          <div class="swiper-wrapper">
            <article
              v-for="item in items"
              :key="item.id"
              class="swiper-slide c-feature-carousel__slide"
            >
              <a
                class="c-feature-carousel__card"
                :href="item.href"
                :target="item.target"
                :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined"
                :aria-label="item.linkLabel"
              >
                <AdaptiveImage
                  v-if="item.imageField"
                  class="c-feature-carousel__image"
                  :field="item.imageField"
                  :is-background="false"
                  :show="true"
                  :enable-scroll-detection="false"
                  :show-image-redius="false"
                />
                <div class="c-feature-carousel__shade" />
                <div class="c-feature-carousel__content">
                  <RichText
                    v-if="item.fields.title?.value"
                    class="c-feature-carousel__title"
                    :field="item.fields.title"
                    tag="h3"
                  />
                  <RichText
                    v-if="item.fields.description?.value"
                    class="c-feature-carousel__description"
                    :field="item.fields.description"
                  />
                </div>
                <span class="c-feature-carousel__entry-icon" aria-hidden="true"></span>
              </a>
            </article>
          </div>
        </div>

        <div
          v-show="shouldShowPagination"
          ref="paginationEl"
          class="c-feature-carousel__pagination swiper-pagination"
        />
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import { destroySwiper } from '@/utils/site-utils';
import type { ImageField } from '@/types/sitecore';
import utils from '@/utils';
import 'swiper/css';

interface FeatureCarouselItemFields {
  image?: {
    fields?: {
      desktopImage?: ImageField;
      tabletImage?: ImageField;
      mobileImage?: ImageField;
    };
  };
  title?: { value?: string };
  description?: { value?: string; editable?: string };
  link?: { value?: { href?: string; text?: string; title?: string; target?: string } };
}

interface FeatureCarouselItem {
  id: string;
  fields?: FeatureCarouselItemFields | null;
}

interface NormalizedItem extends FeatureCarouselItem {
  fields: FeatureCarouselItemFields;
  imageField?: {
    fields: {
      desktopImage: ImageField;
      tabletImage: ImageField;
      mobileImage: ImageField;
    };
  };
  href: string;
  target?: string;
  linkLabel: string;
}

const props = defineProps({
  fields: {
    type: Object,
    default: null,
  },
  rendering: {
    type: Object,
    default: null,
  },
  params: {
    type: Object,
    default: null,
  },
  dataSource: {
    type: String,
    default: '',
  },
});

let swiper: Swiper | null = null;
const swiperEl = ref<HTMLElement | null>(null);
const paginationEl = ref<HTMLElement | null>(null);
const viewportWidth = ref(0);

const rawItems = computed<FeatureCarouselItem[]>(() => {
  const cards = props.fields?.items;
  return Array.isArray(cards) ? cards : [];
});

const getAdaptiveImageField = (image?: FeatureCarouselItemFields['image'], alt = '') => {
  if (image?.fields?.desktopImage?.value?.src) {
    const desktopImage = image.fields.desktopImage;
    const tabletImage = image.fields.tabletImage?.value?.src
      ? image.fields.tabletImage
      : desktopImage;
    const mobileImage = image.fields.mobileImage?.value?.src
      ? image.fields.mobileImage
      : desktopImage;

    return {
      fields: {
        desktopImage,
        tabletImage,
        mobileImage,
      },
    };
  }

  return undefined;
};

const items = computed<NormalizedItem[]>(() =>
  rawItems.value.reduce<NormalizedItem[]>((result, item, index) => {
    const itemFields = item.fields;
    if (!itemFields) return result;

    const title = itemFields.title?.value || `Feature ${index + 1}`;
    const linkValue = itemFields.link?.value || {};

    result.push({
      ...item,
      fields: itemFields,
      imageField: getAdaptiveImageField(itemFields.image, title),
      href: linkValue.href || '#',
      target: linkValue.target,
      linkLabel: linkValue.text || linkValue.title || title,
    });

    return result;
  }, [])
);

const rootClass = computed(() => [
  utils.overall.baseComClass(props?.fields, 'c-feature-carousel'),
  {
    'c-feature-carousel--compact': items.value.length < 5,
    'c-feature-carousel--three': items.value.length === 3,
    'c-feature-carousel--four': items.value.length === 4,
  },
]);
const rootStyle = computed(() => utils.overall?.baseComStyle?.(props.fields) || {});
const isDesktop = computed(() => viewportWidth.value >= 1200);
const isCompact = computed(() => items.value.length < 5);
const shouldShowPagination = computed(
  () => items.value.length > 1 && (!isDesktop.value || !isCompact.value)
);
const slidesPerViewDesktop = computed(() => (isCompact.value ? items.value.length : 5));

const updateViewportWidth = () => {
  viewportWidth.value = window.innerWidth;
};

const buildSwiper = () => {
  if (!swiperEl.value || !items.value.length) return;

  destroySwiper(swiper, true, true);
  swiper = new Swiper(swiperEl.value, {
    modules: [Pagination],
    // Keep a little more of the next card visible on mobile so the cards do not dominate the screen.
    slidesPerView: 1.8,
    spaceBetween: 18,
    centeredSlides: false,
    watchOverflow: true,
    resizeObserver: true,
    observer: true,
    observeParents: true,
    pagination:
      shouldShowPagination.value && paginationEl.value
        ? {
            el: paginationEl.value,
            clickable: true,
            bulletElement: 'span',
          }
        : false,
    breakpoints: {
      744: {
        slidesPerView: 'auto',
        spaceBetween: 24,
      },
      1200: {
        slidesPerView: slidesPerViewDesktop.value,
        spaceBetween: 28,
      },
      1440: {
        slidesPerView: slidesPerViewDesktop.value,
        spaceBetween: 32,
      },
    },
  });
};

watch(
  () => [items.value.length, shouldShowPagination.value, slidesPerViewDesktop.value],
  () => nextTick(() => buildSwiper())
);

onMounted(() => {
  updateViewportWidth();
  window.addEventListener('resize', updateViewportWidth, { passive: true });
  nextTick(() => buildSwiper());
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewportWidth);
  destroySwiper(swiper, true, true);
});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-feature-carousel {
  background: $white;
  overflow: hidden;

  &__inner {
    width: 100%;
    padding-bottom: 20px;
  }

  &__swiper {
    overflow: visible;
    padding: 0;
  }

  &__slide {
    height: auto;
  }

  &__card {
    position: relative;
    display: block;
    overflow: hidden;
    border-radius: 24px;
    color: $white;
    text-decoration: none;
    background: #6f8f73;
    isolation: isolate;
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 352 494' preserveAspectRatio='none'%3E%3Cpath fill='white' d='M0 66C0 46.118 16.118 30 36 30H174C196 30 196 0 220 0H326C340.359 0 352 11.641 352 26V470C352 483.255 341.255 494 328 494H24C10.745 494 0 483.255 0 470V66Z'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 352 494' preserveAspectRatio='none'%3E%3Cpath fill='white' d='M0 66C0 46.118 16.118 30 36 30H174C196 30 196 0 220 0H326C340.359 0 352 11.641 352 26V470C352 483.255 341.255 494 328 494H24C10.745 494 0 483.255 0 470V66Z'/%3E%3C/svg%3E");
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
  }

  &__shade {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  &__image {
    position: relative;
    z-index: 0;
    display: block;
    width: 100%;
    transform: scale(1.01);
    transition: transform 0.45s ease;

    > div,
    .e-background-image,
    .e-background-image__img,
    img {
      width: 100%;
      height: auto;
    }

    img {
      display: block;
    }
  }

  &__shade {
    z-index: 1;
    background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.5) 100%);
  }

  &__content {
    position: absolute;
    left: 22px;
    right: 22px;
    bottom: 24px;
    z-index: 3;
  }

  &__entry-icon {
    position: absolute;
    right: 22px;
    bottom: 22px;
    z-index: 4;
    display: block;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.94);
    color: #739b78;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

    &::before,
    &::after {
      content: '';
      position: absolute;
      box-sizing: border-box;
    }

    &::before {
      left: 14px;
      top: 23px;
      width: 24px;
      height: 3px;
      border-radius: 2px;
      background: currentColor;
      transform: rotate(-45deg);
    }

    &::after {
      right: 13px;
      top: 15px;
      width: 14px;
      height: 15px;
      border-top: 3px solid currentColor;
      border-right: 3px solid currentColor;
    }
  }

  &__title {
    margin: 0 0 14px;
    color: $white;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.16;
    letter-spacing: 0;
  }

  &__description {
    padding-right: 60px;
    color: rgba(255, 255, 255, 0.88);
    font-size: 17px;
    font-weight: 400;
    line-height: 1.42;
    letter-spacing: 0;

    p {
      margin: 0;
    }
  }

  &__card:hover &__image {
    transform: scale(1.055);
  }

  &__pagination.swiper-pagination,
  &__swiper + &__pagination.swiper-pagination-bullets.swiper-pagination-horizontal {
    position: relative;
    left: auto;
    right: auto;
    top: auto;
    bottom: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: auto;
    margin-top: 40px;
    transform: none;
  }

  .swiper-pagination-bullet {
    display: block;
    width: 36px;
    height: 4px;
    margin: 0;
    border-radius: 0;
    background: #e8e8e8;
    opacity: 1;
    transform: skewX(-28deg);
    transition:
      width 0.25s ease,
      background-color 0.25s ease;
  }

  .swiper-pagination-bullet-active {
    width: 54px;
    background: #6f9273;
  }

  @include tablet {
    &__description {
      padding-right: 0;
    }

    &__entry-icon {
      display: none;
    }

    &__inner {
      width: 100%;
    }

    &__swiper .swiper-wrapper {
      justify-content: center;
    }

    &__slide {
      width: clamp(180px, 21vw, 212px);
    }

    &--compact {
      .c-feature-carousel__swiper {
        width: 100%;
        max-width: 100%;
        margin: 0 auto;
      }

      .c-feature-carousel__swiper .swiper-wrapper {
        justify-content: center;
      }

      .c-feature-carousel__slide {
        flex-shrink: 0;
        width: clamp(216px, 23vw, 272px);
      }
    }

    &--four {
      .c-feature-carousel__slide {
        width: calc((100% - 72px) / 4);
        min-width: 0;
        max-width: none;
      }
    }

    &--three {
      .c-feature-carousel__slide {
        width: clamp(260px, 27vw, 320px);
        min-width: 260px;
        max-width: 320px;
      }
    }
  }

  @include desktop {
    &__inner {
      width: 100%;
    }

    &__swiper .swiper-wrapper {
      justify-content: flex-start;
    }

    &__slide {
      width: auto;
    }

    &--compact {
      .c-feature-carousel__swiper {
        width: 100%;
        max-width: 100%;
        margin: 0 auto;
      }

      .c-feature-carousel__swiper .swiper-wrapper {
        justify-content: center;
      }

      .c-feature-carousel__slide {
        width: clamp(240px, 17vw, 304px);
      }

      .c-feature-carousel__pagination {
        display: none;
      }
    }

    &--four {
      .c-feature-carousel__slide {
        width: calc((100% - 84px) / 4);
        min-width: 0;
        max-width: none;
      }
    }

    &--three {
      .c-feature-carousel__slide {
        width: clamp(300px, 22vw, 360px);
        min-width: 300px;
        max-width: 360px;
      }
    }
  }

  @include desktop-large {
    &__inner {
      width: 100%;
    }

    &--four {
      .c-feature-carousel__slide {
        width: calc((100% - 96px) / 4);
      }
    }
  }
}
</style>
